import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StreakMilestoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestone: number;
  habitTitle?: string;
}

const milestoneConfig: Record<number, { emoji: string; title: string; subtitle: string }> = {
  7: {
    emoji: '🔥',
    title: 'Week Warrior!',
    subtitle: "You've built a solid foundation. Keep the momentum going!",
  },
  30: {
    emoji: '💪',
    title: 'Monthly Master!',
    subtitle: "30 days of dedication. You're unstoppable!",
  },
  90: {
    emoji: '🏆',
    title: 'Quarter Champion!',
    subtitle: "90 days! You've mastered the art of consistency.",
  },
};

export const StreakMilestoneModal = ({
  isOpen,
  onClose,
  milestone,
  habitTitle,
}: StreakMilestoneModalProps) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const config = milestoneConfig[milestone];

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  const handleShare = () => {
    const text = `🎉 I just hit a ${milestone}-day streak on my habit "${habitTitle || 'daily practice'}"! Building consistency one day at a time. #MomentumEQ #PersonalGrowth`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}&summary=${encodeURIComponent(text)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  if (!config) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md border-primary/30 bg-gradient-to-b from-background to-background/95 overflow-hidden">
        <DialogTitle className="sr-only">{config.title}</DialogTitle>
        
        {/* Confetti Background */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-confetti-fall"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`,
                }}
              >
                <div
                  className={cn(
                    "w-2 h-2 rounded-full",
                    i % 5 === 0 && "bg-primary",
                    i % 5 === 1 && "bg-accent",
                    i % 5 === 2 && "bg-warning",
                    i % 5 === 3 && "bg-success",
                    i % 5 === 4 && "bg-secondary"
                  )}
                  style={{
                    transform: `rotate(${Math.random() * 360}deg)`,
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        {/* Content */}
        <div className="flex flex-col items-center text-center py-8 px-4 relative z-10">
          {/* Animated Emoji */}
          <div className="relative mb-6">
            <span 
              className="text-7xl animate-milestone-emoji block"
              role="img" 
              aria-label="milestone emoji"
            >
              {config.emoji}
            </span>
            {/* Glow effect */}
            <div className="absolute inset-0 blur-2xl opacity-50 bg-gradient-to-r from-primary to-accent rounded-full" />
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent animate-fade-in">
            {config.title}
          </h2>

          {/* Streak Count */}
          <div className="flex items-center gap-2 mb-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <span className="text-5xl font-bold text-foreground">{milestone}</span>
            <span className="text-lg text-muted-foreground">day streak!</span>
          </div>

          {/* Subtitle */}
          <p className="text-muted-foreground mb-6 max-w-xs animate-fade-in" style={{ animationDelay: '0.4s' }}>
            {config.subtitle}
          </p>

          {/* Habit Name */}
          {habitTitle && (
            <p className="text-sm text-muted-foreground/70 mb-6 animate-fade-in" style={{ animationDelay: '0.5s' }}>
              Habit: <span className="text-foreground font-medium">{habitTitle}</span>
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <Button
              variant="outline"
              onClick={handleShare}
              className="gap-2"
            >
              <Share2 className="w-4 h-4" />
              Share on LinkedIn
            </Button>
            <Button onClick={onClose}>
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
